import { useMutation } from '@apollo/client';
import { useTranslations } from '@vocab/react';

import { ErrorMessage } from 'src/components/ErrorMessage/ErrorMessage';
import { JobItemLayout } from 'src/components/JobItemLayout/JobItemLayout';
import { MostRecentStatus } from 'src/components/MostRecentStatus/MostRecentStatus';
import type {
  AppliedJob,
  DeleteAppliedJobMutation,
  DeleteAppliedJobMutationVariables,
} from 'src/types/graphql';
import { getEvent } from 'src/utils';

import { logError } from '../../modules/error-logger/browserLog';

import translations from './AppliedJob.vocab';
import { DELETE_APPLIED_JOB } from './delete-applied-job.graphql';

export interface Props {
  job: AppliedJob;
  index: number;
  openView: (id: string) => void;
  openNotes: (id: string) => void;
}

// TODO: coverLetterUrl * resumeUrl
export const AppliedJobItem = (props: Props) => {
  const { t } = useTranslations(translations);
  const { index, job } = props;
  const { isExternal } = job;
  const viewedEvent = getEvent(job?.events, 'Viewed');
  const notSuitableEvent = getEvent(job?.events, 'NotSuitable');

  const [deleteAppliedJob, deleteAppliedJobState] = useMutation<
    DeleteAppliedJobMutation,
    DeleteAppliedJobMutationVariables
  >(DELETE_APPLIED_JOB, {
    onError() {
      logError({
        type: 'delete-applied-job-error',
        message: 'Unable to remove job',
      });
    },
    refetchQueries: ['GetAppliedJobs'], // using the query name will let apollo client to reuse lastest used variables
    awaitRefetchQueries: true,
  });

  return (
    <JobItemLayout
      index={index}
      errorMessage={
        deleteAppliedJobState.error ? (
          <ErrorMessage
            index={index}
            errorMessage={t(
              `Sorry, we couldn’t save your changes. Can we try again?`,
            )}
          />
        ) : null
      }
      dateApplied={
        <MostRecentStatus
          isExternal={isExternal}
          appliedDate={job.appliedAt?.shortAbsoluteLabel}
          appliedDateHiddenVisually={job.appliedAt?.dateTimeUtc}
          viewedDate={viewedEvent?.timestamp?.shortAbsoluteLabel}
          viewedDateHiddenVisually={viewedEvent?.timestamp?.dateTimeUtc}
          notSuitableDate={notSuitableEvent?.timestamp?.shortAbsoluteLabel}
          notSuitableDateHiddenVisually={
            notSuitableEvent?.timestamp?.dateTimeUtc
          }
        />
      }
      job={job}
      type="applied"
      deleteMyActivityJob={deleteAppliedJob}
      openView={props.openView}
      openNotes={props.openNotes}
    />
  );
};
